import { useCallback, useEffect, useRef, useState } from 'react';

import { SELECTION_SUMMARY_ID } from './InvoiceSelectionSummary';

export const SELECTABLE_ROW_ATTR = 'data-selectable-row';

interface Range {
    from: number;
    to: number;
}

interface Drag {
    anchor: number;
    moved: boolean;
}

const INTERACTIVE = 'button, a, input, select, textarea, [role="button"]';

const isInteractive = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest(INTERACTIVE);

const rowIndexOf = (target: EventTarget | null): number | null => {
    const row = target instanceof Element ? target.closest(`[${SELECTABLE_ROW_ATTR}]`) : null;
    return row ? Number(row.getAttribute(SELECTABLE_ROW_ATTR)) : null;
};

const keepsSelection = (target: EventTarget | null) =>
    target instanceof Element
    && !!target.closest(`#${SELECTION_SUMMARY_ID}, [${SELECTABLE_ROW_ATTR}]`);

export const useRowDragSelection = (onRowClick: (index: number) => void) => {
    const [range, setRange] = useState<Range | null>(null);
    const drag = useRef<Drag | null>(null);
    const onRowClickRef = useRef(onRowClick);
    onRowClickRef.current = onRowClick;

    const clear = useCallback(() => setRange(null), []);

    useEffect(() => {
        const finish = (e: MouseEvent) => {
            const current = drag.current;
            drag.current = null;
            if (current && !current.moved && rowIndexOf(e.target) === current.anchor) {
                onRowClickRef.current(current.anchor);
            }
        };
        document.addEventListener('mouseup', finish);
        return () => document.removeEventListener('mouseup', finish);
    }, []);

    useEffect(() => {
        if (!range) return undefined;
        const onOutside = (e: MouseEvent) => { if (!keepsSelection(e.target)) setRange(null); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setRange(null); };
        document.addEventListener('mousedown', onOutside);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [range]);

    const rowProps = (index: number) => ({
        [SELECTABLE_ROW_ATTR]: index,
        onMouseDown: (e: React.MouseEvent) => {
            if (e.button !== 0 || isInteractive(e.target)) return;
            e.preventDefault();
            setRange(null);
            drag.current = { anchor: index, moved: false };
        },
        onMouseEnter: () => {
            const current = drag.current;
            if (!current) return;
            if (index !== current.anchor) current.moved = true;
            if (current.moved) setRange({ from: current.anchor, to: index });
        },
    });

    const isSelected = (index: number) =>
        !!range && index >= Math.min(range.from, range.to) && index <= Math.max(range.from, range.to);

    const selectedIndexes = (): number[] => {
        if (!range) return [];
        const start = Math.min(range.from, range.to);
        return Array.from({ length: Math.abs(range.to - range.from) + 1 }, (_, i) => start + i);
    };

    return { rowProps, isSelected, selectedIndexes, clear, hasSelection: !!range };
};
